import { Modal } from "@tyrannosaurustech/ui";
import { useState } from "react";
import styled from "styled-components";
import { usePushNoteToConnectedMutation } from "../../../redux/reduxQuery/medicalProviderApi";
import useToast from "../../../hooks/useToast";
import { Theme } from "../../../components/atoms/theme";
import { ParagraphS, SubtitlesS } from "../../../components/atoms/Typography";
import { TextArea } from "../../../components/atoms/TextArea";

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled(SubtitlesS)`
  margin-bottom: 0.5rem;
  display: block;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${Theme.colors.neutrals_300};
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_000};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

interface MassNotePushModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  provider: { id: string; name: string };
  connectionsCount: number;
  refetchFunction: () => void;
}

const noteFieldOptions = [
  { value: "notes", label: "Sender Notes" },
  { value: "connectionNotes", label: "Connection Notes" },
  { value: "followUpNotes", label: "Follow Up Notes" },
  { value: "vettingNotes", label: "Vetting Notes" },
];

export const MassNotePushModal = ({
  open,
  setOpen,
  provider,
  connectionsCount,
  refetchFunction,
}: MassNotePushModalProps) => {
  const [noteField, setNoteField] =
    useState<"notes" | "connectionNotes" | "followUpNotes" | "vettingNotes">(
      "notes"
    );
  const [noteContent, setNoteContent] = useState("");
  const [appendMode, setAppendMode] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pushNoteToConnected] = usePushNoteToConnectedMutation();
  const toast = useToast();

  const handleSubmit = async () => {
    if (!noteContent.trim()) {
      toast.warn("Please enter note content");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await pushNoteToConnected({
        providerId: provider.id,
        noteField,
        noteContent: noteContent.trim(),
        appendMode,
      }).unwrap();

      if (result.failed.length > 0) {
        toast.warn(
          `Updated ${result.updated} providers, ${result.failed.length} failed`
        );
      } else {
        toast.success(`Note pushed to ${result.updated} connected providers`);
      }

      refetchFunction();
      handleClose();
    } catch (error) {
      toast.error("Failed to push note to connected providers");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNoteField("notes");
    setNoteContent("");
    setAppendMode(true);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      setOpen={(shouldOpen) => {
        if (!shouldOpen) {
          handleClose();
        }
      }}
      loading={isSubmitting}
      title="Push Note to Connected Providers"
      confirmText={`Push to ${connectionsCount} Provider${
        connectionsCount !== 1 ? "s" : ""
      }`}
      onConfirm={handleSubmit}
      noConfirm={!noteContent.trim()}
    >
      <div style={{ width: "100%" }}>
        <ParagraphS
          color={Theme.colors.gray_200}
          style={{ marginBottom: "1rem" }}
        >
          Push a note to all providers connected to{" "}
          <strong>{provider.name}</strong>. This will update the selected note
          field on {connectionsCount} connected provider
          {connectionsCount !== 1 ? "s" : ""}.
        </ParagraphS>

        <FormGroup>
          <Label>Note Field</Label>
          <Select
            value={noteField}
            onChange={(e) =>
              setNoteField(
                e.target.value as
                  | "notes"
                  | "connectionNotes"
                  | "followUpNotes"
                  | "vettingNotes"
              )
            }
          >
            {noteFieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Note Content</Label>
          <TextArea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Enter the note to push to connected providers..."
            width="100%"
            lineHeight="1.5"
          />
        </FormGroup>

        <FormGroup>
          <Label>Mode</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="appendMode"
                checked={appendMode}
                onChange={() => setAppendMode(true)}
              />
              <span>Append to existing notes</span>
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="appendMode"
                checked={!appendMode}
                onChange={() => setAppendMode(false)}
              />
              <span>Replace existing notes</span>
            </RadioLabel>
          </RadioGroup>
          <ParagraphS
            color={Theme.colors.gray_200}
            style={{ marginTop: "0.5rem", fontSize: "12px" }}
          >
            {appendMode
              ? "The note will be added above any existing content (newer notes on top)."
              : "Warning: This will replace all existing content in the selected note field."}
          </ParagraphS>
        </FormGroup>
      </div>
    </Modal>
  );
};
